import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";

/**
 * Convertit la clé publique VAPID (URL-safe base64) en Uint8Array
 * pour l'API PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/**
 * Bouton qui demande la permission et abonne l'utilisateur aux push notifications PWA.
 * S'auto-désactive si l'utilisateur est déjà abonné, ou si le navigateur ne supporte pas.
 */
export default function PushNotificationToggle({ compact = false, className = "" }) {
  const [supported] = useState(isPushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        if (!reg) return;
        const existing = await reg.pushManager.getSubscription();
        setSubscribed(!!existing);
      } catch (_) { /* ignore */ }
    })();
  }, [supported]);

  const subscribe = async () => {
    if (!supported) {
      toast.error("Votre navigateur ne supporte pas les notifications push.");
      return;
    }
    setBusy(true);
    try {
      // 1) Demande la permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permission refusée. Activez les notifications dans votre navigateur.");
        return;
      }
      // 2) Enregistre le service worker (idempotent)
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      // 3) Récupère la clé publique VAPID
      let publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!publicKey) {
        const r = await api.get("/push/vapid-public-key");
        publicKey = r.data.public_key;
      }
      if (!publicKey) throw new Error("Clé VAPID indisponible.");
      // 4) Abonnement
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      // 5) Envoi au backend
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        expirationTime: subJson.expirationTime || null,
        keys: subJson.keys,
      });
      // 6) Notification de bienvenue
      await api.post("/push/test").catch(() => {});
      setSubscribed(true);
      toast.success("Notifications activées 🔔");
    } catch (e) {
      console.error(e);
      toast.error("Impossible d'activer les notifications.");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications désactivées.");
    } catch (_) {
      toast.error("Erreur lors de la désactivation.");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  if (compact) {
    return (
      <button
        type="button"
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={busy || permission === "denied"}
        data-testid="push-toggle-compact"
        className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
          subscribed
            ? "bg-mint-100 text-mint-700 border-mint-200"
            : "bg-white text-plum-700/65 border-cream-200 hover:bg-cream-50"
        } ${className}`}
        title={subscribed ? "Notifications activées" : "Activer les notifications push"}
      >
        {busy ? <BellRing size={15} className="animate-pulse" /> : subscribed ? <BellRing size={15} /> : <Bell size={15} />}
      </button>
    );
  }

  return (
    <div className={`phenix-card p-4 sm:p-5 flex items-start gap-3 ${className}`} data-testid="push-toggle-card">
      <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${subscribed ? "bg-mint-100 text-mint-700" : "bg-sun-100 text-sun-700"}`}>
        {subscribed ? <BellRing size={20} strokeWidth={2.2} /> : <Bell size={20} strokeWidth={2.2} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-[14px] text-plum-900 leading-tight">
          {subscribed ? "Notifications activées" : "Activer les notifications"}
        </div>
        <p className="text-[12px] text-plum-700/65 mt-1">
          {subscribed
            ? "Vous recevrez les nouveaux sondages et bons de vos commerçants directement sur votre appareil."
            : "Recevez une alerte dès qu'un nouveau sondage ou bon est lancé près de chez vous."}
        </p>
        {permission === "denied" && (
          <p className="mt-2 text-[11px] text-pink-600 font-bold">
            Permission refusée. Activez-les dans les paramètres du navigateur.
          </p>
        )}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={subscribed ? unsubscribe : subscribe}
            disabled={busy || permission === "denied"}
            data-testid="push-toggle-btn"
            className={`btn-pill h-9 px-4 text-[12px] font-bold transition-colors ${
              subscribed
                ? "bg-cream-100 text-plum-700 hover:bg-cream-200"
                : "bg-plum-700 hover:bg-plum-800 text-white"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {busy ? "…" : subscribed ? (<><BellOff size={12} className="mr-1.5 inline" /> Désactiver</>) : (<><Bell size={12} className="mr-1.5 inline" /> Activer</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
